import { useEffect, useMemo, useRef, useState } from "react";
import {
  CustomDialog,
  Header,
  Heading,
  Content,
  Footer,
  Text,
  ButtonGroup,
  Button,
  CloseButton,
  SearchField,
  Picker,
  PickerItem,
  Collection,
  CardView,
  Card,
  CardPreview,
  SkeletonCollection,
  IllustratedMessage,
  Badge,
} from "@react-spectrum/s2";
import { useHover } from "@react-aria/interactions";
import PropTypes from "prop-types";

import { S3Video } from "@app/components/S3Video";
import { useFootage, useFootageIndustries } from "@app/hooks/footage";

import FilmstripIcon from "@react-spectrum/s2/illustrations/linear/Filmstrip";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 300;
const ALL_INDUSTRIES = "__all__";

const FootageCard = ({ id, item }) => {
  const videoRef = useRef(null);
  const { hoverProps } = useHover({
    onHoverChange: (isHovered) => {
      const video = videoRef.current;

      if (!video) {
        return;
      }

      if (isHovered) {
        video.play?.().catch(() => {});
      } else {
        video.pause?.();
      }
    },
  });

  return (
    <Card
      {...hoverProps}
      id={id ?? item.fileName}
      textValue={item.description || item.fileName}
    >
      <CardPreview>
        <S3Video
          ref={videoRef}
          filePath={item.fileName}
          isLoading={item.isPlaceholder}
          autoPlay={false}
          muted
          loop
          playsInline
          preload="metadata"
          className={style({
            width: "full",
            aspectRatio: "16 / 9",
            objectFit: "cover",
            pointerEvents: "none",
          })}
        />
      </CardPreview>
      <Content>
        <Text slot="description">{item.description || item.fileName}</Text>
      </Content>
      {!!item.industry && (
        <Footer>
          <div className={style({ gridArea: "description" })}>
            <Badge size="XS" variant="neutral" fillStyle="subtle">
              {item.industry}
            </Badge>
          </div>
        </Footer>
      )}
    </Card>
  );
};

FootageCard.propTypes = {
  id: PropTypes.string,
  item: PropTypes.shape({
    fileName: PropTypes.string.isRequired,
    description: PropTypes.string,
    industry: PropTypes.string,
    isPlaceholder: PropTypes.bool,
  }).isRequired,
};

export const FootageDialog = ({ defaultSelectedFilePath, onSelect }) => {
  const [search, setSearch] = useState("");
  const [industry, setIndustry] = useState("");
  const [debouncedFilters, setDebouncedFilters] = useState({
    q: "",
    industry: "",
  });
  const [selectedKeys, setSelectedKeys] = useState(
    defaultSelectedFilePath ? new Set([defaultSelectedFilePath]) : new Set(),
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedFilters({ q: search, industry });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [search, industry]);

  const filters = useMemo(
    () => ({
      ...(debouncedFilters.q && { q: debouncedFilters.q }),
      ...(debouncedFilters.industry && { industry: debouncedFilters.industry }),
      pageSize: PAGE_SIZE,
    }),
    [debouncedFilters],
  );

  const { data: industries = [] } = useFootageIndustries();

  const industryItems = useMemo(
    () => [
      { id: ALL_INDUSTRIES, label: "All industries" },
      ...industries.map((name) => ({ id: name, label: name })),
    ],
    [industries],
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
    error,
  } = useFootage(filters);

  const items = data?.pages?.flatMap((page) => page.footage) ?? [];
  const total = data?.pages?.at(-1)?.pagination?.total ?? 0;

  const loadingState = isError
    ? "error"
    : isLoading
      ? "loading"
      : isFetchingNextPage
        ? "loadingMore"
        : "idle";

  const selectedKey = selectedKeys.values().next().value;
  const selectedItem = items.find((item) => item.fileName === selectedKey);

  return (
    <CustomDialog
      isDismissable
      styles={style({
        maxWidth: "90vw",
        width: {
          default: "full",
          sm: 1200,
        },
      })}
    >
      {({ close }) => (
        <div
          className={style({
            size: "full",
            display: "grid",
            gridTemplateAreas: [
              "header header",
              "filters filters",
              "content content",
              "footer buttonGroup",
            ],
            gridTemplateRows: ["auto", "auto", "1fr", "auto"],
            rowGap: 16,
          })}
        >
          <Header
            styles={style({
              gridArea: "header",
              display: "flex",
              alignItems: "center",
              gap: 12,
              font: "title-2xl",
              marginTop: 0,
            })}
          >
            <Text>Select footage</Text>
          </Header>

          <div
            className={style({
              gridArea: "filters",
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
            })}
          >
            <SearchField
              aria-label="Search footage"
              placeholder="Search description, key elements, attributes"
              value={search}
              onChange={setSearch}
              onClear={() => setSearch("")}
              styles={style({ width: 320 })}
            />
            <Picker
              aria-label="Filter by industry"
              items={industryItems}
              value={industry || ALL_INDUSTRIES}
              onChange={(key) => setIndustry(key === ALL_INDUSTRIES ? "" : key)}
              styles={style({ width: 220 })}
            >
              {(item) => (
                <PickerItem id={item.id} textValue={item.label}>
                  {item.label}
                </PickerItem>
              )}
            </Picker>
            <Text styles={style({ font: "body-sm", alignSelf: "center" })}>
              {total} total
            </Text>
          </div>

          <CardView
            aria-label="Available footage"
            selectionMode="single"
            selectionStyle="checkbox"
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            loadingState={loadingState}
            onLoadMore={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            renderEmptyState={() => (
              <IllustratedMessage size="L">
                <FilmstripIcon />
                <Heading>
                  {isError ? "Failed to load footage" : "No footage found"}
                </Heading>
                <Content>
                  {isError
                    ? (error?.message ??
                      "There was an error loading the footage. Please try again.")
                    : "Try adjusting your search or industry filter."}
                </Content>
              </IllustratedMessage>
            )}
            styles={style({
              gridArea: "content",
              maxHeight: "60vh",
              overflowY: "auto",
            })}
          >
            <Collection items={loadingState === "loading" ? [] : items}>
              {(item) => <FootageCard id={item.fileName} item={item} />}
            </Collection>
            {(loadingState === "loading" || loadingState === "loadingMore") && (
              <SkeletonCollection>
                {() => (
                  <FootageCard
                    item={{
                      fileName: `placeholder-${Math.random()}`,
                      description: "Loading footage",
                      isPlaceholder: true,
                    }}
                  />
                )}
              </SkeletonCollection>
            )}
          </CardView>

          <Footer styles={style({ gridArea: "footer" })}></Footer>
          <ButtonGroup
            styles={style({ gridArea: "buttonGroup", justifySelf: "end" })}
          >
            <Button variant="secondary" onPress={close}>
              <Text>Cancel</Text>
            </Button>
            <Button
              variant="accent"
              isDisabled={
                !selectedItem || selectedKey === defaultSelectedFilePath
              }
              onPress={() => {
                onSelect?.(selectedItem);
                close();
              }}
            >
              <Text>Use footage</Text>
            </Button>
          </ButtonGroup>
          <CloseButton
            styles={style({
              position: "absolute",
              top: 12,
              insetEnd: 12,
            })}
          />
        </div>
      )}
    </CustomDialog>
  );
};

FootageDialog.propTypes = {
  defaultSelectedFilePath: PropTypes.string,
  onSelect: PropTypes.func,
};
